import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { FadeUp, MaskedReveal, SectionLabel } from "@/components/Motion";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TEMPORARY: pre-launch mode hides all payment/checkout flows and shows a
// "Let me know when subscriptions open" waitlist form instead. Flip this to
// `false` (and nothing else) once Razorpay is fully configured to restore the
// original checkout end-to-end. All existing payment code below is preserved.
const LAUNCH_MODE = false;

// Only three purchase options. Both editions share the same pricing.
const PACKAGES = [
  {
    id: "single",
    title: "Single Edition",
    price: "₹699",
    subtitle: "One individual edition of The Safe Mama Editorial.",
    perks: ["One issue, delivered by post", "All five enclosures", "A one-time purchase — no auto-renewal"],
  },
  {
    id: "3month",
    title: "3-Month Plan",
    price: "₹1,799",
    subtitle: "Three monthly editions. One payment, no auto-renewal.",
    perks: ["Three issues, delivered monthly", "All five enclosures, each month", "Pause & resume anytime"],
    featured: true,
  },
  {
    id: "6month",
    title: "6-Month Plan",
    price: "₹2,999",
    subtitle: "Six monthly editions. One payment, no auto-renewal.",
    perks: ["Six issues, delivered monthly", "All five enclosures, each month", "Pause & resume anytime"],
  },
];

const EDITIONS = [
  { id: "pregnancy", label: "Pregnancy Edition", accent: "terracotta" },
  { id: "motherhood", label: "Motherhood Edition", accent: "sage" },
];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function WaitlistModal({ open, onClose, defaultEdition }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [edition, setEdition] = useState(defaultEdition || "pregnancy");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { if (open) { setDone(false); setEdition(defaultEdition || "pregnancy"); } }, [open, defaultEdition]);

  if (!open) return null;

  const submit = async (ev) => {
    ev.preventDefault();
    if (!name.trim()) { toast.error("Please enter your first name."); return; }
    if (!email.trim()) { toast.error("Please enter your email."); return; }
    if (!consent) { toast.error("Please tick the consent box to join the launch list."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/waitlist`, { name: name.trim(), email: email.trim(), edition, consent });
      setDone(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not join the list. Please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm" data-testid="waitlist-modal" role="dialog" aria-modal="true">
      <div className="relative bg-cream border border-ink/10 rounded-md max-w-lg w-full p-8 md:p-10 shadow-[0_30px_80px_-30px_rgba(46,38,32,0.5)]">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-ink/60 hover:text-ink text-2xl leading-none"
          data-testid="waitlist-close"
        >
          ×
        </button>

        {done ? (
          <div data-testid="waitlist-done">
            <div className="overline text-terracotta">Thank you</div>
            <h3 className="mt-4 font-serif italic text-4xl md:text-5xl text-ink leading-tight">You're on the list.</h3>
            <p className="mt-6 font-serif text-lg md:text-xl text-ink/75 leading-relaxed">
              We'll write to you as soon as subscriptions open.
            </p>
            <button onClick={onClose} className="btn-pill mt-10" data-testid="waitlist-close-done">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="waitlist-form">
            <div className="overline text-ink/60">Launch list</div>
            <h3 className="font-serif italic text-3xl md:text-4xl text-ink leading-tight">
              Let me know when subscriptions open.
            </h3>

            <div>
              <label className="overline text-ink/60 block mb-2">First name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-transparent border-b border-ink/30 py-2 font-serif text-xl text-ink outline-none focus:border-ink transition-colors"
                placeholder="Ananya"
                data-testid="waitlist-name"
                autoFocus
              />
            </div>

            <div>
              <label className="overline text-ink/60 block mb-2">Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-ink/30 py-2 font-serif text-xl text-ink outline-none focus:border-ink transition-colors"
                placeholder="you@post.com"
                data-testid="waitlist-email"
                required
              />
            </div>

            <div>
              <label className="overline text-ink/60 block mb-3">Which edition are you interested in?</label>
              <div className="flex gap-6">
                {["pregnancy", "motherhood"].map((k) => (
                  <label key={k} className="flex items-center gap-2 cursor-pointer" data-testid={`waitlist-edition-${k}`}>
                    <input
                      type="radio"
                      name="edition"
                      value={k}
                      checked={edition === k}
                      onChange={() => setEdition(k)}
                      className="accent-terracotta"
                    />
                    <span className="font-serif italic text-xl text-ink capitalize">{k}</span>
                  </label>
                ))}
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer" data-testid="waitlist-consent-label">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 accent-terracotta"
                data-testid="waitlist-consent"
              />
              <span className="font-serif text-base text-ink/80 leading-relaxed">
                I'd like to hear from The Safe Mama Editorial when subscriptions open.
              </span>
            </label>

            <button
              type="submit"
              disabled={busy}
              className="btn-pill mt-2 disabled:opacity-60"
              data-testid="waitlist-submit"
            >
              {busy ? "Adding you…" : "Join the list"} <ArrowUpRight size={14} />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function MailClub() {
  const search = useLocation().search;
  const preselected = useMemo(() => {
    const q = new URLSearchParams(search).get("edition");
    return q === "motherhood" || q === "pregnancy" ? q : "pregnancy";
  }, [search]);

  const [edition, setEdition] = useState(preselected);
  useEffect(() => setEdition(preselected), [preselected]);

  const [provider, setProvider] = useState("razorpay");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setStateVal] = useState("");
  const [pincode, setPincode] = useState("");
  const [preferences, setPreferences] = useState("");
  const [loadingId, setLoadingId] = useState(null);
  const [rzEnabled, setRzEnabled] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/payments/razorpay/config`)
      .then((r) => setRzEnabled(!!r.data.enabled))
      .catch(() => {});
  }, []);

  const validateDelivery = () => {
    if (!name.trim()) return "Please enter your name.";
    if (!email.trim()) return "Please enter your email.";
    if (!phone.trim() || phone.replace(/\D/g, "").length < 7) return "Please enter a valid phone number.";
    if (!addressLine1.trim()) return "Please enter your delivery address.";
    if (!city.trim()) return "Please enter your city.";
    if (!state.trim()) return "Please enter your state.";
    if (!pincode.trim() || pincode.replace(/\D/g, "").length < 4) return "Please enter a valid PIN code.";
    return null;
  };

  const buildDelivery = () => ({
    phone,
    address: {
      line1: addressLine1,
      line2: addressLine2,
      city,
      state,
      pincode,
      country: "IN",
    },
  });

  const startStripe = async (pkgId) => {
    const err = validateDelivery();
    if (err) { toast.error(err); return; }
    setLoadingId(pkgId);
    try {
      const res = await axios.post(`${API}/payments/stripe/checkout`, {
        package_id: pkgId,
        edition,
        origin_url: window.location.origin,
        email,
        name,
        preferences,
        ...buildDelivery(),
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoadingId(null);
    }
  };

  const startRazorpay = async (pkgId) => {
    const err = validateDelivery();
    if (err) { toast.error(err); return; }
    setLoadingId(pkgId);
    const ok = await loadRazorpay();
    if (!ok) {
      toast.error("Could not load Razorpay.");
      setLoadingId(null);
      return;
    }
    try {
      // All plans are ONE-TIME payments (Razorpay Orders). No recurring billing.
      const res = await axios.post(`${API}/payments/razorpay/order`, {
        package_id: pkgId,
        edition,
        email,
        name,
        preferences,
        ...buildDelivery(),
      });
      const { order_id, amount, currency, key_id } = res.data;
      const rzp = new window.Razorpay({
        key: key_id,
        amount,
        currency,
        order_id,
        name: "The Safe Mama Editorial",
        description: `${edition === "pregnancy" ? "Pregnancy" : "Motherhood"} Edition — ${pkgId}`,
        theme: { color: "#C4694A" },
        prefill: { name, email, contact: phone },
        handler: async (r) => {
          try {
            await axios.post(`${API}/payments/razorpay/verify`, {
              razorpay_order_id: r.razorpay_order_id,
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_signature: r.razorpay_signature,
            });
            window.location.href = `/payment/success?session_id=${r.razorpay_order_id}`;
          } catch {
            toast.error("Payment verification failed.");
          }
        },
        modal: { ondismiss: () => setLoadingId(null) },
      });
      rzp.open();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Razorpay not available.";
      toast.error(msg);
      setLoadingId(null);
    }
  };

  const start = (pkgId) => (provider === "stripe" ? startStripe(pkgId) : startRazorpay(pkgId));

  return (
    <div className="pt-10 md:pt-16" data-testid="mail-club-page">
      <section className="editorial-container">
        <SectionLabel number="No. 001" label="Mail Club" />
        <h1 className="mt-6 font-serif text-[13vw] sm:text-[9vw] md:text-[7vw] lg:text-[6rem] leading-[1] tracking-tight text-ink pb-4">
          <MaskedReveal text="Subscribe to the" as="span" className="block" delay={0.1} animate />
          <MaskedReveal text="post." as="span" className="block italic text-terracotta" delay={0.4} animate />
        </h1>
        <FadeUp delay={0.8}>
          <p className="mt-8 font-serif text-xl md:text-2xl leading-[1.4] text-ink/75 max-w-2xl">
            The Safe Mama Editorial is a monthly print publication for pregnancy and motherhood.
            Two editions, one for each season — same enclosures, adapted to your stage. Pause or
            cancel any time by email.
          </p>
        </FadeUp>
      </section>

      {/* Edition selector */}
      <section className="mt-20 md:mt-24 editorial-container" data-testid="edition-selector">
        <div className="hairline pt-6">
          <div className="overline text-ink/60 mb-6">Choose your edition</div>
          <div className="flex flex-wrap gap-x-10 gap-y-4">
            {EDITIONS.map((e) => {
              const active = edition === e.id;
              return (
                <button
                  key={e.id}
                  onClick={() => setEdition(e.id)}
                  className={`font-serif italic text-3xl md:text-4xl leading-tight transition-colors ${
                    active
                      ? e.accent === "terracotta"
                        ? "text-terracotta"
                        : "text-sage"
                      : "text-ink/30 hover:text-ink/70"
                  }`}
                  data-testid={`edition-${e.id}`}
                >
                  {e.label}
                  {active && <span className="ml-3 align-middle inline-block h-1.5 w-1.5 rounded-full bg-current" />}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pre-launch waitlist notice */}
      {LAUNCH_MODE && (
        <section className="mt-16 md:mt-20 editorial-container" data-testid="launch-notice">
          <div className="border border-ink/15 bg-cream/60 rounded-md px-6 md:px-10 py-8 md:py-10 max-w-3xl">
            <div className="overline text-terracotta">Coming soon</div>
            <h2 className="mt-3 font-serif italic text-3xl md:text-4xl text-ink leading-tight">
              Subscriptions are opening shortly.
            </h2>
            <p className="mt-4 font-serif text-lg md:text-xl text-ink/75 leading-relaxed">
              The first editions of The Safe Mama Editorial are almost ready for post.
              Leave your email below and we'll write to you as soon as subscriptions open.
            </p>
          </div>
        </section>
      )}

      {/* Contact & delivery */}
      {!LAUNCH_MODE && (
      <section className="mt-16 md:mt-20 editorial-container" data-testid="subscribe-form">
        <div className="overline text-ink/60 mb-8">Where should we post it?</div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-x-12 md:gap-y-10">
          <div className="md:col-span-6">
            <label className="overline text-ink/60 block mb-3">Your name</label>
            <input
              type="text"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              className="w-full bg-transparent border-b border-ink/30 py-3 font-serif text-2xl text-ink outline-none focus:border-ink transition-colors"
              placeholder="Ananya"
              data-testid="input-name"
            />
          </div>
          <div className="md:col-span-6">
            <label className="overline text-ink/60 block mb-3">Your email</label>
            <input
              type="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              className="w-full bg-transparent border-b border-ink/30 py-3 font-serif text-2xl text-ink outline-none focus:border-ink transition-colors"
              placeholder="you@post.com"
              data-testid="input-email"
            />
          </div>

          <div className="md:col-span-6">
            <label className="overline text-ink/60 block mb-3">Phone number</label>
            <input
              type="tel"
              value={phone}
              onChange={(ev) => setPhone(ev.target.value)}
              className="w-full bg-transparent border-b border-ink/30 py-3 font-serif text-2xl text-ink outline-none focus:border-ink transition-colors"
              placeholder="+91 98765 43210"
              data-testid="input-phone"
            />
          </div>
          <div className="md:col-span-6">
            <label className="overline text-ink/60 block mb-3">PIN code</label>
            <input
              type="text"
              value={pincode}
              onChange={(ev) => setPincode(ev.target.value)}
              className="w-full bg-transparent border-b border-ink/30 py-3 font-serif text-2xl text-ink outline-none focus:border-ink transition-colors"
              placeholder="560001"
              data-testid="input-pincode"
            />
          </div>

          <div className="md:col-span-12">
            <label className="overline text-ink/60 block mb-3">Delivery address</label>
            <input
              type="text"
              value={addressLine1}
              onChange={(ev) => setAddressLine1(ev.target.value)}
              className="w-full bg-transparent border-b border-ink/30 py-3 font-serif text-2xl text-ink outline-none focus:border-ink transition-colors"
              placeholder="House / flat number, street"
              data-testid="input-address-line1"
            />
          </div>
          <div className="md:col-span-12">
            <label className="overline text-ink/60 block mb-3">Address, line 2 <span className="text-ink/30 normal-case tracking-normal">(optional)</span></label>
            <input
              type="text"
              value={addressLine2}
              onChange={(ev) => setAddressLine2(ev.target.value)}
              className="w-full bg-transparent border-b border-ink/30 py-3 font-serif text-2xl text-ink outline-none focus:border-ink transition-colors"
              placeholder="Landmark, area, apartment"
              data-testid="input-address-line2"
            />
          </div>

          <div className="md:col-span-6">
            <label className="overline text-ink/60 block mb-3">City</label>
            <input
              type="text"
              value={city}
              onChange={(ev) => setCity(ev.target.value)}
              className="w-full bg-transparent border-b border-ink/30 py-3 font-serif text-2xl text-ink outline-none focus:border-ink transition-colors"
              placeholder="Bengaluru"
              data-testid="input-city"
            />
          </div>
          <div className="md:col-span-6">
            <label className="overline text-ink/60 block mb-3">State</label>
            <input
              type="text"
              value={state}
              onChange={(ev) => setStateVal(ev.target.value)}
              className="w-full bg-transparent border-b border-ink/30 py-3 font-serif text-2xl text-ink outline-none focus:border-ink transition-colors"
              placeholder="Karnataka"
              data-testid="input-state"
            />
          </div>

          <div className="md:col-span-12 mt-6">
            <label className="overline text-ink/60 block mb-3">
              Your interests · what would you love to read? <span className="text-ink/30 normal-case tracking-normal">(optional)</span>
            </label>
            <textarea
              value={preferences}
              onChange={(ev) => setPreferences(ev.target.value)}
              rows={3}
              className="w-full bg-transparent border-b border-ink/30 py-3 font-serif text-xl text-ink outline-none focus:border-ink transition-colors resize-none"
              placeholder="e.g. postpartum nutrition, returning to work, sleep, second pregnancies…"
              data-testid="input-preferences"
            />
          </div>
        </div>

        <div className="mt-12 overline text-ink/60" data-testid="payment-note">
          Secure payment via <span className="normal-case tracking-normal font-serif italic text-ink/85">Razorpay</span> — UPI, cards & netbanking.
        </div>
      </section>
      )}

      {/* Plans */}
      <section className="mt-20 md:mt-24 editorial-container">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-16">
          {PACKAGES.map((p, i) => (
            <FadeUp key={p.id} delay={i * 0.06}>
              <div
                className="relative flex flex-col h-full border-t border-ink/20 pt-8"
                data-testid={`plan-card-${p.id}`}
              >
                <div className="flex items-baseline justify-between">
                  <div className="overline text-ink/50">{p.title}</div>
                  {p.featured && <div className="overline text-terracotta">Reader's pick</div>}
                </div>
                <div className="mt-6 font-serif text-5xl md:text-6xl leading-[1] text-ink" data-testid={`plan-price-${p.id}`}>
                  {p.price}
                </div>
                <p className="mt-4 font-serif italic text-xl text-ink/70">{p.subtitle}</p>
                <ul className="mt-8 space-y-3 flex-1">
                  {p.perks.map((perk, k) => (
                    <li key={k} className="font-serif text-lg text-ink/80 leading-relaxed">
                      · {perk}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => (LAUNCH_MODE ? setWaitlistOpen(true) : start(p.id))}
                  disabled={!LAUNCH_MODE && loadingId === p.id}
                  className="btn-pill mt-12 self-start disabled:opacity-60"
                  data-testid={LAUNCH_MODE ? `waitlist-cta-${p.id}` : `subscribe-btn-${p.id}`}
                >
                  {LAUNCH_MODE
                    ? <>Let me know when subscriptions open <ArrowUpRight size={14} /></>
                    : <>{loadingId === p.id ? "Opening checkout…" : `Pay · ${p.price}`} <ArrowUpRight size={14} /></>}
                </button>
              </div>
            </FadeUp>
          ))}
        </div>

        {!LAUNCH_MODE && (
        <p className="mt-14 text-sm font-sans text-ink/70 leading-relaxed max-w-3xl" data-testid="checkout-legal-consent">
          By placing your order, you agree to our{" "}
          <Link to="/terms-and-conditions" className="underline decoration-ink/40 hover:decoration-ink text-ink" data-testid="checkout-terms-link">
            Terms &amp; Conditions
          </Link>{" "}
          and acknowledge our{" "}
          <Link to="/privacy-policy" className="underline decoration-ink/40 hover:decoration-ink text-ink" data-testid="checkout-privacy-link">
            Privacy Policy
          </Link>
          .
        </p>
        )}
      </section>

      <section className="mt-32 editorial-container">
        <FadeUp>
          <div className="border-t border-ink/15 pt-10 grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { t: "Shipped by post", b: "Every issue is packed by hand and sealed in a paper envelope." },
              { t: "Pause anytime", b: "Visit Manage My Plan to pause and resume — no charges, no auto-renewal." },
              { t: "Ships across India", b: "India-wide delivery. Written to you in English." },
            ].map((x, i) => (
              <div key={i}>
                <div className="overline text-ink/60">{`0${i + 1}`}</div>
                <div className="mt-3 font-serif text-2xl italic text-ink">{x.t}</div>
                <p className="mt-2 font-serif text-lg text-ink/70">{x.b}</p>
              </div>
            ))}
          </div>
        </FadeUp>
      </section>
      <WaitlistModal open={waitlistOpen} onClose={() => setWaitlistOpen(false)} defaultEdition={edition} />
    </div>
  );
}
